import { useState } from 'react';
import { Box, Typography, Avatar } from '@mui/material';

type MediationRevenue = {
  mediation: string;
  revenue: number;
};

type Props = {
  top5MediationStats: MediationRevenue[];
  bottom5MediationStats: MediationRevenue[];
};

const krw = (n: number) =>
  new Intl.NumberFormat('ko-KR', { style: 'currency', currency: 'KRW', maximumFractionDigits: 0 }).format(n);

function Pill({ item }: { item: MediationRevenue }) {
  const [imageError, setImageError] = useState(false);
  const mediationImagePath = `/img/adpopcorn/mediation/${item.mediation}.png`;

  const handleImageError = () => {
    setImageError(true);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: { xs: 1, sm: 1.25 },
        p: { xs: 1, sm: 1.25 },
        borderRadius: 9999,
        border: '1px solid #E0E3E7',
        bgcolor: '#fff',
        minWidth: 0,
        overflow: 'hidden',
      }}
    >
      <Avatar
        src={!imageError ? mediationImagePath : undefined}
        onError={handleImageError}
        sx={{
          width: { xs: 28, sm: 36, md: 40 },
          height: { xs: 28, sm: 36, md: 40 },
          bgcolor: '#EEF2F6',
          color: '#455A64',
          fontWeight: 700,
          fontSize: { xs: 12, sm: 14 },
          flexShrink: 0,
        }}
      >
        {item.mediation?.[0] ?? '?'}
      </Avatar>

      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography variant="subtitle2" noWrap title={item.mediation}>
          {item.mediation}
        </Typography>
        <Typography variant="subtitle2" fontWeight={700}>
          {krw(item.revenue)}
        </Typography>
      </Box>
    </Box>
  );
}

const GRID5 = {
  display: 'grid',
  gridTemplateColumns: 'repeat(5, minmax(0, 1fr))',
  gap: { xs: 1, sm: 1.5 },
} as const;

function Section({
  title,
  items,
  emptyText,
}: {
  title: string;
  items: MediationRevenue[];
  emptyText: string;
}) {
  return (
    <Box sx={{ mt: 1, overflowX: 'hidden' }}>
      <Typography variant="subtitle1" sx={{ mb: 1 }}>
        {title}
      </Typography>
      <Box sx={GRID5}>
        {items.length === 0 ? (
          <Box
            sx={{
              gridColumn: '1 / -1',
              p: 1.25,
              borderRadius: 1,
              border: '1px dashed #E0E3E7',
              bgcolor: '#FAFAFA',
            }}
            aria-live="polite"
          >
            <Typography variant="body2" color="text.secondary">
              {emptyText}
            </Typography>
          </Box>
        ) : (
          <>
            {items.slice(0, 5).map((it, index) => (
              <Pill key={`top-${it.mediation}-${index}`} item={it} />
            ))}
            {Array.from({ length: Math.max(0, 5 - items.length) }).map((_, i) => (
              <Box key={`empty-${i}`} />
            ))}
          </>
        )}
      </Box>
    </Box>
  );
}

export default function AdpopcornMediationDeltaTop5({ top5MediationStats, bottom5MediationStats }: Props) {
  return (
    <Box sx={{ mt: 2, overflowX: 'hidden' }}>
      <Section title="매출 TOP 미디에이션" items={top5MediationStats} emptyText="데이터가 없습니다." />
      <Section title="매출 LOW 미디에이션" items={bottom5MediationStats} emptyText="데이터가 없습니다." />
    </Box>
  );
}
